// apps/web/src/store/localStorageMiddleware.ts
import type { Middleware } from '@reduxjs/toolkit';
import type { RootState } from './types';
import { markProfileTouched } from '../utils/acceptance';
import {
	activeFinancialKey,
	isAnonymousSession,
} from '../lib/sessionMode';
import { scheduleProfilePush, schedulePlanSync } from '../lib/syncApi';

// Async-thunk lifecycle actions fire on mere page loads (Dashboard/Simulate
// mount → fetchProfile; autosave → saveProfile). They are NOT user edits, so
// they must not flip the "profile personalized" flag.
const NON_EDIT_FINANCIAL = /^financial\/(fetchProfile|saveProfile)\//;

export const localStorageMiddleware: Middleware<object, RootState> =
	(store) => (next) => (action) => {
		const result = next(action);
		const state = store.getState();

		if (typeof action === 'object' && action !== null && 'type' in action) {
			const type = (action as { type: string }).type;
			if (type.startsWith('financial/')) {
				try {
					const { _syncStatus: _, ...persistable } = state.financial;
					localStorage.setItem(
						activeFinancialKey(),
						JSON.stringify(persistable),
					);
				} catch {
					// quota exceeded or private browsing
				}
				// Authed: mirror the document to the server (debounced).
				// Lifecycle actions are echoes, not edits — the fetch has
				// already reconciled with the server.
				if (
					!isAnonymousSession() &&
					!NON_EDIT_FINANCIAL.test(type)
				) {
					const { _syncStatus: _t, ...doc } = state.financial;
					scheduleProfilePush(doc, state.financial._lastUpdated);
				}
				// A deliberate edit (not a load/save lifecycle action) means the user
				// has personalized their numbers — unlocks the direct Simulate handoff.
				if (
					!NON_EDIT_FINANCIAL.test(type) &&
					!isAnonymousSession()
				) {
					markProfileTouched();
				}
			}

			// Plans: reducers persist locally themselves; here we diff-push
			// to the server (debounced). Fetch actions prime the shadow in
			// the thunk, so diffing them is a no-op.
			if (type.startsWith('scenarios/') && !isAnonymousSession()) {
				schedulePlanSync(state.scenarios.scenarios);
			}
		}

		return result;
	};
